/**
 * Retrieves a dataset attribute from an HTMLElement safely.
 *
 * It supports a fallback in case the attribute is not present.
 * @example
 * ```ts
 * const myElement = document.querySelector<HTMLElement>('#my-element-id');
 * const value = getDatasetSafe<string>(myElement, 'myKey', 'defaultValue');
 * ```
 * @param element - The HTMLElement to retrieve the dataset attribute from.
 * @param key - The key of the dataset attribute to retrieve.
 * @param fallback - An optional fallback value if the attribute is not present.
 * @returns The value of the dataset attribute or the fallback value.
 */
export function getDatasetSafe<R, T extends HTMLElement = HTMLElement>(element: T, key: string): R | undefined;
export function getDatasetSafe<R, T extends HTMLElement = HTMLElement>(element: T, key: string, fallback: R): R;
export function getDatasetSafe<R, T extends HTMLElement = HTMLElement>(element: T, key: string, fallback?: R): R | undefined {
  const value = element.dataset[key];

  if (typeof fallback === 'boolean') {
    return asBoolean(value) as R;
  }

  return value !== undefined ? (value as R) : fallback;
}

export function getParsedArrayDatasetSafe<R extends Array<any>, T extends HTMLElement = HTMLElement>(
  element: T,
  key: string,
  fallback: R = [] as unknown as R,
) {
  const value = getDatasetSafe<string>(element, key);
  if (value) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed as R;
      }
      return fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function asBoolean(value: string | undefined): boolean {
  if (value === undefined) {
    return false;
  }

  switch (value.toLowerCase()) {
    case 'yes':
    case '1':
    case 'y':
    case 'true':
    case '': // if the value exists it is also true.
      return true;

    default:
      return false;
  }
}
